import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import {
  DataGrid,
} from "@mui/x-data-grid";

import FactoryStockService from "../../services/FactoryStockService";

const FactoryStockUpload = () => {

  // ==========================
  // STATES
  // ==========================

  const [file, setFile] = useState(null);

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [uploading, setUploading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(50);

  const [rowCount, setRowCount] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);

  const [deleteDialog, setDeleteDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ==========================
  // SHOW SNACKBAR
  // ==========================

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // ==========================
  // FETCH TABLE DATA
  // ==========================

  const fetchFactoryStock = async (
    currentPage = page,
    currentSearch = search
  ) => {

    try {

      setLoading(true);

      const response = await FactoryStockService.factoryStockList({
        page: currentPage + 1,
        page_size: pageSize,
        search: currentSearch,
      });

      const data = response.data;

      setRows(data.results || []);

      setRowCount(data.total || 0);

    } catch (error) {

      console.log(error);

      showSnackbar(
        "Unable to load stock.",
        "error"
      );

    } finally {

      setLoading(false);

    }

  };

  // ==========================
  // INITIAL LOAD
  // ==========================

  useEffect(() => {

    fetchFactoryStock();

  }, [page, pageSize]);

  // ==========================
  // FILE CHANGE
  // ==========================

  const handleFileChange = (event) => {

    if (!event.target.files.length)
      return;

    setFile(event.target.files[0]);

  };

  // ==========================
  // UPLOAD
  // ==========================

  const handleUpload = async () => {

    if (!file) {

      showSnackbar(
        "Please select an Excel file.",
        "warning"
      );

      return;
    }

    try {

      setUploading(true);

      const formData = new FormData();

      formData.append(
        "excel_file",
        file
      );

      await FactoryStockService.uploadFactoryStock(formData);

      showSnackbar(
        "Upload Successful."
      );

      setFile(null);

      fetchFactoryStock();

    } catch (error) {
      console.log("UPLOAD ERROR:", error);

      const responseData = error?.response?.data;

      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Upload Failed.";

      showSnackbar(errorMessage, "error");

    } finally {
      setUploading(false);

    }

  };

  // ==========================
  // SEARCH
  // ==========================

  const handleSearch = () => {

    setPage(0);

    fetchFactoryStock(
      0,
      search
    );

  };

  // ==========================
  // RESET
  // ==========================

  const handleReset = () => {

    setSearch("");

    setPage(0);

    fetchFactoryStock(
      0,
      ""
    );

  };

  // ==========================
  // DELETE
  // ==========================

  // const handleDelete = async () => {

  //   try {

  //     await FactoryStockService.deleteFactoryStock({
  //       ids: selectedRows,
  //     });

  //     showSnackbar(
  //       "Deleted Successfully."
  //     );

  //     setDeleteDialog(false);

  //     setSelectedRows([]);

  //     fetchFactoryStock();

  //   } catch (error) {

  //     console.log(error);

  //     showSnackbar(
  //       "Delete Failed.",
  //       "error"
  //     );

  //   }

  // };

  const handleUntagClick = () => {
  if (selectedRows.length === 0) {
    showSnackbar("Please select an IMEI.", "warning");
    return;
  }

  if (selectedRows.length > 1) {
    showSnackbar("Please select only one IMEI to untag.", "warning");
    return;
  }

  setDeleteDialog(true);
};

  const handleUntag = async () => {

    try {

      await FactoryStockService.untaggedIMEI({
        ids: selectedRows,
      });

      showSnackbar(
        "Untagged Successfully."
      );

      setDeleteDialog(false);

      setSelectedRows([]);

      fetchFactoryStock();

    } catch (error) {

      console.log(error);

      showSnackbar(
        "Untag Failed.",
        "error"
      );

    }

  };

  // ==========================
  // BOOTSTRAP REQUEST
  // ==========================

  const handleBootstrapRequest = async () => {

    if (!selectedRows.length) {

      showSnackbar(
        "Please select records.",
        "warning"
      );

      return;
    }

    try {

      const response =
        await FactoryStockService.sendBootstrapRequest({
          ids: selectedRows,
        });

      const blob = new Blob(
        [response.data],
        {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      const url =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        "bootstrap_request.xlsx";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(
        url
      );

      showSnackbar(
        "Bootstrap Request Sent."
      );

      fetchFactoryStock();

    } catch (error) {

      console.log("BOOTSTRAP ERROR:", error);

      let responseData = error?.response?.data;

      // API returns error response as Blob because responseType is blob
      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          responseData = JSON.parse(text);
        } catch (parseError) {
          console.log("Failed to parse Blob error response:", parseError);
        }
      }

      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Bootstrap Request Failed.";

      showSnackbar(errorMessage, "error");
    }

  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");

    // Place sample file inside public/sample/
    link.href = "../../sample/factory_upload_sample.xlsx";

    // You can also use factory_stock_sample.xlsx
    link.download = "factory_upload_sample.xlsx";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ==========================
  // DATAGRID COLUMNS
  // ==========================

  const columns = [

    {
      field: "id",
      headerName: "ID",
      width: 80,
    },

    {
      field: "uid",
      headerName: "UID",
      width: 150,
    },

    {
      field: "imei",
      headerName: "IMEI",
      width: 180,
    },

    {
      field: "iccid_primary",
      headerName: "ICCID",
      width: 220,
    },

    {
      field: "basket_name",
      headerName: "Basket",
      width: 170,
    },

    {
      field: "provider_sim_status",
      headerName: "Provider Status",
      width: 200,
    },
    // {
    //   field: "device_feature_name",
    //   headerName: "Device Feature",
    //   width: 200,
    // },

    {
      field: "stock_status_update_date",
      headerName: "Updated Date",
      width: 220,
      valueGetter: (params) => {
        const value = params.row?.stock_status_update_date;

        if (!value) return "-";

        return new Date(value).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      },
    },

    {
      field: "bootstrap_activation_request",
      headerName: "Bootstrap Request",
      width: 220,
    },

    {
      field: "bootstrap_activation_requested_at",
      headerName: "Bootstrap Requested At",
      width: 220,
      valueGetter: (params) => {
        const value = params.row?.bootstrap_activation_requested_at;

        if (!value) return "-";

        return new Date(value).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      },
    },

  ];

  // ==========================
  // RETURN STARTS IN PART-2
  // ==========================

  return (
    <Box p={3}>

      {/* =========================
          Upload Card
    ========================== */}

      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
        }}
      >
        <CardContent>

          <Typography
            variant="h5"
            fontWeight="bold"
            color="primary"
          >
            Upload IMEI / ICCID pairing file
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            mt={1}
            mb={3}
          >
            Exactly two columns:
            <strong> IMEI</strong>,
            <strong> ICCID</strong>.
            Whole file fails together.
            If any ICCID doesn't match an inventory row,
            nothing is saved.
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems="center"
          >

            <Button
              variant="outlined"
              component="label"
              sx={{
                minWidth: 180,
              }}
            >
              Choose File

              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
              />

            </Button>

            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
                color: "text.secondary",
              }}
            >
              {file
                ? file.name
                : "No file selected"}
            </Typography>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDownloadSample}
            >
              Download Sample
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={
                uploading ||
                !file
              }
              onClick={handleUpload}
              sx={{
                minWidth: 130,
              }}
            >
              {uploading ? (
                <CircularProgress
                  size={22}
                  color="inherit"
                />
              ) : (
                "Upload"
              )}
            </Button>

          </Stack>

        </CardContent>
      </Card>

      {/* =========================
          Search Card
    ========================== */}

      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
        }}
      >

        <CardContent>

          <Typography
            variant="h6"
            fontWeight="bold"
            mb={3}
          >
            Search / Filter
          </Typography>

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
          >

            <TextField
              fullWidth
              placeholder="Search uid / imei / iccid / basket..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                minWidth: 120,
              }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                minWidth: 120,
              }}
            >
              Reset
            </Button>

            <Button
              variant="outlined"
              color="error"
              disabled={
                selectedRows.length === 0
              }
              onClick={handleUntagClick}
              sx={{
                minWidth: 170,
              }}
            >
              Untag IMEI
            </Button>

            <Button
              variant="contained"
              color="success"
              disabled={
                selectedRows.length === 0
              }
              onClick={
                handleBootstrapRequest
              }
              sx={{
                minWidth: 270,
              }}
            >
              Send Bootstrap
              Activation Request
            </Button>

          </Stack>

        </CardContent>

      </Card>

      {/* =========================
      Matched Stock Table
========================= */}

      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
        }}
      >
        <CardContent>

          <Typography
            variant="h6"
            fontWeight="bold"
            mb={2}
          >
            Matched Stock
          </Typography>

          <Box
            sx={{
              width: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },
            }}
          >

            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              checkboxSelection
              disableRowSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={rowCount}
              pageSizeOptions={[25, 50, 100]}
              paginationModel={{
                page,
                pageSize,
              }}
              onPaginationModelChange={(model) => {
                setPage(model.page);
                setPageSize(model.pageSize);
              }}
              onRowSelectionModelChange={(ids) =>
                setSelectedRows(ids)
              }
              rowSelectionModel={selectedRows}
              getRowId={(row) => row.id}
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      height: 250,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: 18,
                      color: "gray",
                    }}
                  >
                    No Data Available
                  </Box>
                ),
              }}
            />

          </Box>

        </CardContent>
      </Card>

      {/* =========================
      Delete Dialog
========================= */}

      <Dialog
        open={deleteDialog}
        onClose={() =>
          setDeleteDialog(false)
        }
        maxWidth="xs"
        fullWidth
      >

        <DialogTitle>

          Delete Records

        </DialogTitle>

        <DialogContent>

          <Typography>

            Are you sure you want to delete

            <strong>
              {" "}
              {selectedRows.length}{" "}
            </strong>

            selected record(s)?

          </Typography>

        </DialogContent>

        <DialogActions>

          <Button
            onClick={() =>
              setDeleteDialog(false)
            }
          >
            Cancel
          </Button>

          {/* <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
          >
            Delete
          </Button> */}

          <Button
            color="error"
            variant="contained"
            onClick={handleUntag}
          >
            Untag IMEI
          </Button>

        </DialogActions>

      </Dialog>

      {/* =========================
      Snackbar
========================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() =>
          setSnackbar({
            ...snackbar,
            open: false,
          })
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar({
              ...snackbar,
              open: false,
            })
          }
          sx={{
            width: "100%",
          }}
        >

          {snackbar.message}

        </Alert>

      </Snackbar>


    </Box>
  );
};

export default FactoryStockUpload;